import { escapeHtml } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
import { mountShell } from '../components/Shell.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import { formatCrowns } from '../util/formatBig.js';
import { showConfirmDialog } from '../components/ConfirmDialog.js';
import { titleChipHtml } from '../components/Title.js';
import { craftBoxHtml, craftProgress, CRAFT_BOX_CSS } from '../util/craft.js';
import type { InventoryItemDto, OccupantDto, CharacterDto } from '@rex-mortis/shared';

interface FlashState {
  kind: 'success' | 'danger' | 'warn';
  message: string;
}

const PICK_SLUGS = new Set(['manacle_pick']);

let countdownTimer: number | null = null;

function clearCountdown(): void {
  if (countdownTimer !== null) {
    window.clearInterval(countdownTimer);
    countdownTimer = null;
  }
}

/** Bust toll — keep in sync with server DUNGEON.bustVigorPctOfMax / bustVigorMin. */
function bustCost(vigorMax: number): number {
  return Math.max(5, Math.ceil(vigorMax * 0.2));
}

export async function renderDungeon(root: HTMLElement): Promise<void> {
  let inventory: InventoryItemDto[] = [];
  let occupants: OccupantDto[] = [];
  let breakoutResolveCost = 50;
  let gaolcraftLevel = 0;
  try {
    const me = await api.me();
    store.set({ account: me.account, character: me.character });
    const inv = await api.listInventory();
    inventory = inv.items;
    const occ = await api.listDungeonOccupants();
    occupants = occ.occupants;
    breakoutResolveCost = occ.breakoutResolveCost;
    gaolcraftLevel = occ.gaolcraftLevel;
  } catch {
    // tolerated
  }

  let flash: FlashState | null = null;

  mountShell(root, (main) => {
    const renderMain = () => {
      const char = store.get().character;
      if (!char) {
        main.innerHTML = '<p>Loading…</p>';
        return;
      }

      const inDungeon = char.status === 'DUNGEON';
      const medicalActive =
        !!char.medicalCooldownUntil &&
        Date.parse(char.medicalCooldownUntil) > Date.now();

      const picks = inventory.filter((it) => PICK_SLUGS.has(it.slug));

      main.innerHTML = `
        <img class="page-banner" src="/img/banners/dungeon.webp" alt="" />
        <h1>The Dungeon</h1>
        <p style="font-style: italic; color: var(--c-ink-soft); margin-top: -8px;">
          Damp stone, dimmer hopes.
        </p>

        <div id="flash"></div>

        <div class="panel">
          <h2>${inDungeon ? 'You are imprisoned' : 'You are free'}</h2>
          <dl class="kv">
            <dt>Status</dt>
            <dd>${
              inDungeon
                ? `Dungeon — <span id="cd">${formatRemaining(char.statusUntil)}</span>`
                : 'No-one is looking for you. Probably.'
            }</dd>
            <dt>Vigor</dt><dd>${char.vigor} / ${char.vigorMax}</dd>
            <dt>Cunning</dt><dd>${char.cunning}</dd>
            <dt>Crowns on hand</dt><dd>${formatCrowns(char.crowns)}</dd>
          </dl>
        </div>

        ${
          inDungeon
            ? `
              <div class="panel">
                <h2>Escape</h2>
                <p style="color: var(--c-ink-soft); font-style: italic;">
                  Three roads out: bribe a guard, slip your manacles, or break for the wall.
                  The gaoler prices bail by the size of your fortune, not your story.
                </p>
                <div style="display:flex; gap: var(--s-3); flex-wrap: wrap; margin-top: var(--s-3);">
                  <button class="btn" id="bust" ${char.vigor < bustCost(char.vigorMax) ? 'disabled' : ''}>
                    Break Out (${bustCost(char.vigorMax)} vigor)
                  </button>
                  <button class="btn ghost" id="bail">Pay Bail</button>
                </div>
              </div>

              <div class="panel">
                <h2>Tools of liberty</h2>
                ${
                  picks.length === 0
                    ? `<p class="empty" style="font-style:italic;color:var(--c-ink-soft)">
                         No picks on hand. The Burglary tier of the misdeeds sometimes drops them.
                       </p>`
                    : `<div id="picks" class="item-grid"></div>`
                }
              </div>`
            : ''
        }

        <div class="panel">
          <h2>The Other Prisoners</h2>
          <p style="color: var(--c-ink-soft); font-style: italic; margin-top: -4px;">
            Spend <strong>${breakoutResolveCost} Resolve</strong> to break a soul free. This is the
            <strong>Gaolcraft</strong> — and the more locks thou pickest, the less Resolve each
            takes (down to 10 at mastery).
          </p>
          ${craftBoxHtml(
            'Gaolcraft',
            'Each breakout earns XP and lowers the next breakout&rsquo;s Resolve cost.',
            gaolcraftLevel,
          )}
          ${
            char.status !== 'OKAY'
              ? `<p class="alert danger">Thou must be free and unhurt thyself before thou canst break others out.</p>`
              : ''
          }
          ${
            occupants.length === 0
              ? `<p class="empty" style="font-style:italic;color:var(--c-ink-soft)">The Dungeon stands empty. A quiet night in the realm.</p>`
              : `<div id="occupants" class="occupant-list">${occupantRowsHtml(occupants, char, breakoutResolveCost)}</div>`
          }
        </div>
      `;

      const style = document.createElement('style');
      style.textContent = `
        .item-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: var(--s-3);
        }
        .item-card {
          background: var(--c-parchment);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          padding: var(--s-3);
        }
        .item-card .row {
          display: flex; justify-content: space-between; align-items: baseline;
        }
        .item-card h3 { margin: 0; font-size: 1rem; }
        .item-card .qty {
          font-family: var(--font-numeric);
          color: var(--c-oxblood);
          font-weight: bold;
        }
        .item-card .blurb {
          font-style: italic;
          color: var(--c-ink-soft);
          font-size: 0.88rem;
          margin: 4px 0 8px 0;
        }
        .occupant-list {
          display: flex;
          flex-direction: column;
          gap: var(--s-2);
        }
        .occupant-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: var(--s-3);
          padding: var(--s-2) var(--s-3);
          background: var(--c-parchment);
          border: 1px solid var(--c-rule-soft);
          border-radius: var(--radius);
        }
        .occupant-id { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
        .occupant-name {
          font-family: var(--font-display);
          font-weight: 600;
          color: var(--c-ink);
        }
        .occupant-meta {
          font-family: var(--font-numeric);
          font-size: 0.82rem;
          color: var(--c-ink-soft);
        }
        .occupant-row .btn { flex: 0 0 auto; }
        /* Phones: the fixed-width Break Out button squeezed the name column, so a
           long title chip (e.g. "First of the Council") overflowed across it.
           Stack the row — name/title on top, button on its own line below — and
           trim the button so nothing overlaps however long the title runs. */
        @media (max-width: 540px) {
          .occupant-row { flex-direction: column; align-items: stretch; gap: var(--s-2); }
          .occupant-row .btn { width: 100%; font-size: 0.9rem; padding: 7px 10px; }
        }
        .craft-box { margin-bottom: var(--s-3); }
        ${CRAFT_BOX_CSS}
      `;
      main.appendChild(style);

      const flashRoot = main.querySelector<HTMLDivElement>('#flash')!;
      if (flash) {
        const div = document.createElement('div');
        div.className = `alert ${flash.kind === 'warn' ? 'danger' : flash.kind}`;
        div.textContent = flash.message;
        flashRoot.appendChild(div);
      }

      // Bust button
      const bustBtn = main.querySelector<HTMLButtonElement>('#bust');
      bustBtn?.addEventListener('click', async () => {
        bustBtn.disabled = true;
        try {
          const r = await api.bustDungeon();
          store.set({ character: r.character });
          if (r.success) {
            flash = {
              kind: 'success',
              message: `You slip the guards (${pct(r.chance)}). The night air is cold and welcome.`,
            };
          } else {
            flash = {
              kind: 'danger',
              message: `Caught. (${pct(r.chance)}). +${r.extensionSeconds}s on the sentence.`,
            };
          }
        } catch (err) {
          const msg = err instanceof HttpError ? err.message : 'Attempt failed.';
          flash = { kind: 'danger', message: msg };
        } finally {
          anchored(renderMain);
        }
      });

      // Bail button
      const bailBtn = main.querySelector<HTMLButtonElement>('#bail');
      bailBtn?.addEventListener('click', async () => {
        bailBtn.disabled = true;
        try {
          const r = await api.bailDungeon();
          store.set({ character: r.character });
          flash = {
            kind: 'success',
            message: `Bail paid: ${formatCrowns(r.cost)} crowns. Walk free.`,
          };
        } catch (err) {
          const msg = err instanceof HttpError ? err.message : 'Bail failed.';
          flash = { kind: 'danger', message: msg };
        } finally {
          anchored(renderMain);
        }
      });

      // Pick cards
      const grid = main.querySelector<HTMLDivElement>('#picks');
      if (grid && inDungeon) {
        for (const it of picks) {
          const card = document.createElement('div');
          card.className = 'item-card';
          card.innerHTML = `
            <div class="row">
              <h3>${escapeHtml(it.name)}</h3>
              <span class="qty">×${it.qty}</span>
            </div>
            <p class="blurb">${escapeHtml(it.blurb)}</p>
            <button class="btn" data-use="${it.slug}" ${medicalActive ? 'disabled' : ''}>
              ${medicalActive ? 'Medical cooldown active' : 'Use'}
            </button>
          `;
          grid.appendChild(card);
        }
        grid.querySelectorAll<HTMLButtonElement>('button[data-use]').forEach((btn) => {
          btn.addEventListener('click', async () => {
            btn.disabled = true;
            try {
              const r = await api.useItem({ itemSlug: btn.dataset.use! });
              store.set({ character: r.character });
              const inv = await api.listInventory();
              inventory = inv.items;
              if (r.removedStatus === 'DUNGEON') {
                flash = {
                  kind: 'success',
                  message: `${r.itemName} did its quiet work. You are out.`,
                };
              } else {
                flash = { kind: 'success', message: `${r.itemName} used.` };
              }
            } catch (err) {
              const msg = err instanceof HttpError ? err.message : 'Use failed.';
              flash = { kind: 'danger', message: msg };
            } finally {
              anchored(renderMain);
            }
          });
        });
      }

      // Break-out buttons — spend Resolve to free another prisoner.
      main.querySelectorAll<HTMLButtonElement>('button[data-breakout]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          if (btn.disabled) return;
          const targetId = btn.dataset.breakout!;
          const targetName = btn.dataset.name ?? 'this prisoner';
          const ok = await showConfirmDialog({
            title: `Break out ${targetName}?`,
            message: `This spends ${breakoutResolveCost} Resolve. You have ${char.resolve}. There is no reward — only the deed.`,
            confirmLabel: 'Yes, break them out',
            cancelLabel: 'Cancel',
            tone: 'gold',
          });
          if (!ok) return;
          btn.disabled = true;
          try {
            const r = await api.breakoutPrisoner(targetId);
            store.set({ character: r.character });
            flash = {
              kind: 'success',
              message: `${r.freedName} walks free. The night swallows them. (−${r.resolveSpent} Resolve · Gaolcraft now level ${craftProgress(r.gaolcraftLevel).level} / 10 · ${r.gaolcraftLevel} souls freed)`,
            };
            const occ = await api.listDungeonOccupants();
            occupants = occ.occupants;
            breakoutResolveCost = occ.breakoutResolveCost;
            gaolcraftLevel = occ.gaolcraftLevel;
          } catch (err) {
            const msg = err instanceof HttpError ? err.message : 'Break-out failed.';
            flash = { kind: 'danger', message: msg };
          } finally {
            anchored(renderMain);
          }
        });
      });

      // Live countdown.
      clearCountdown();
      if (inDungeon && char.statusUntil) {
        countdownTimer = window.setInterval(() => {
          const cd = main.querySelector<HTMLSpanElement>('#cd');
          if (!cd) {
            clearCountdown();
            return;
          }
          const remaining = formatRemaining(char.statusUntil);
          cd.textContent = remaining;
          if (remaining === '0s') {
            clearCountdown();
            void api.me().then((m) => {
              store.set({ account: m.account, character: m.character });
              anchored(renderMain);
            });
          }
        }, 1000);
      }
    };

    anchored(renderMain);
  });

  window.addEventListener('hashchange', clearCountdown, { once: true });
}

function formatRemaining(iso: string | null): string {
  if (!iso) return '0s';
  const ms = Date.parse(iso) - Date.now();
  if (ms <= 0) return '0s';
  const sec = Math.ceil(ms / 1000);
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

function pct(x: number): string {
  return `${Math.round(x * 100)}%`;
}

/**
 * Render the prisoner rows for "The Other Prisoners" panel. Each row's
 * break-out button is disabled (with a reason in the tooltip) when the viewer
 * can't act — they're held themselves, short on Resolve, or it's their own row.
 */
function occupantRowsHtml(
  list: OccupantDto[],
  char: CharacterDto,
  cost: number,
): string {
  const canAct = char.status === 'OKAY';
  return list
    .map((o) => {
      const isSelf = o.characterId === char.id;
      const canBreak = canAct && !isSelf && char.resolve >= cost;
      const reason = isSelf
        ? 'This is you'
        : !canAct
        ? 'Free yourself first'
        : char.resolve < cost
        ? `Need ${cost} Resolve`
        : `Spend ${cost} Resolve to break them out`;
      return `
        <div class="occupant-row">
          <div class="occupant-id">
            <span class="occupant-name"><a href="#/u/${encodeURIComponent(o.characterId)}">${escapeHtml(o.name)}</a>${titleChipHtml(o.activeTitleSlug)}</span>
            <span class="occupant-meta">Level ${o.level} · frees in ${formatRemaining(o.statusUntil)}</span>
          </div>
          <button
            class="btn ${canBreak ? '' : 'ghost'}"
            data-breakout="${o.characterId}"
            data-name="${escapeHtml(o.name)}"
            title="${escapeHtml(reason)}"
            ${canBreak ? '' : 'disabled'}
          >
            Break Out (${cost} Resolve)
          </button>
        </div>`;
    })
    .join('');
}

